import { useRef, useState, useEffect, useCallback } from "react";
import { loadStyles } from "../../../utils/cssLoader";
import { formatRssDate } from "../../../utils/interactivesHelper";
import NextImage from "../../common/NextImage";

/**
 * InstagramCard — square-format card with inline muted video or image.
 * Video autoplays muted when 30%+ visible; pauses when scrolled away.
 */
const InstagramCard = ({ item = {}, loadCSS = true, onClick = null }) => {
  const { description, pubDate, image, video, author, authorImage } = item;
  const videoElRef = useRef(null); // direct reference to the <video> DOM node
  const obsRef = useRef(null);     // IntersectionObserver instance
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    if (loadCSS === true || loadCSS === "true") {
      loadStyles("interactives");
    }
  }, [loadCSS]);

  // Callback ref: fires synchronously when <video> mounts or unmounts.
  const setVideoRef = useCallback((el) => {
    videoElRef.current = el;

    if (obsRef.current) {
      obsRef.current.disconnect();
      obsRef.current = null;
    }
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.play().catch(() => {});
        } else {
          el.pause();
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    obsRef.current = obs;
  }, []); // stable — no deps needed

  const toggleMute = (e) => {
    e.stopPropagation();
    const el = videoElRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
  };

  const [imgFailed, setImgFailed] = useState(false);
  const caption = description || item.title || "";
  if (!video && !image && !caption) return null;

  return (
    <div className="ig-card">
      {/* Square media area — video stays fully interactive, no slider trigger */}
      <div className="ig-card__media">
        {video ? (
          <video
            ref={setVideoRef}
            src={video}
            className="ig-card__video"
            muted
            loop
            playsInline
            preload="metadata"
            poster={image || undefined}
          />
        ) : image && !imgFailed ? (
          <div
            onClick={onClick}
            style={onClick ? { cursor: "pointer", width: "100%", height: "100%" } : undefined}
          >
            <img
              src={image}
              alt={caption}
              className="ig-card__img"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              loading="lazy"
              onError={() => setImgFailed(true)}
            />
          </div>
        ) : (
          <div className="ig-card__media-placeholder" onClick={onClick} style={onClick ? { cursor: "pointer" } : undefined}>
            <svg className="ig-card__placeholder-icon" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
              <circle cx="12" cy="12" r="5" />
              <circle cx="17.5" cy="6.5" r="1.5" fill="white" stroke="none" />
            </svg>
          </div>
        )}

        {/* Gradient overlay */}
        <div className="ig-card__gradient" />

        {/* Mute toggle */}
        {video && (
          <button
            className="ig-card__mute-btn"
            onClick={toggleMute}
            aria-label={muted ? "Unmute" : "Mute"}
          >
            {muted ? "🔇" : "🔊"}
          </button>
        )}

        {/* Video indicator */}
        {video && <span className="ig-card__type-icon">▶</span>}
      </div>

      {/* Footer — clicking opens slider */}
      <div
        className="ig-card__footer"
        onClick={onClick}
        style={onClick ? { cursor: "pointer" } : undefined}
      >
        <div className="ig-card__author-row">
          {authorImage ? (
            <NextImage
              src={authorImage}
              alt={author || ""}
              clz="ig-card__avatar"
              width={28}
              height={28}
            />
          ) : (
            <div className="ig-card__avatar ig-card__avatar--placeholder" />
          )}
          <div className="ig-card__author-info">
            {author && <span className="ig-card__username">{author}</span>}
            {pubDate && (
              <span className="ig-card__date">{formatRssDate(pubDate)}</span>
            )}
          </div>
        </div>

        {caption && (
          <p className="ig-card__caption">
            {caption.length > 100 ? caption.slice(0, 100).trimEnd() + "…" : caption}
          </p>
        )}
      </div>
    </div>
  );
};

export default InstagramCard;
